import { appManager, AppState } from '../manager';
import type {
  DevPlanLaunchSelection,
  DevPlanState,
  DevPlanSummary,
  VegaAttemptPayload,
} from './types';

// GET /api/devplan/plans — the trees this user can pick (published, non-hidden) +
// a per-tree progress summary. Drives the picker + the "is my active tree still
// available" check (multi-tree).
export const fetchDevPlans = appManager.createApi<
  Record<string, never>,
  { plans: DevPlanSummary[] },
  AppState
>('FETCH_DEV_PLANS', {
  method: 'GET',
  path: '/devplan/plans',
  startReducer(state) {
    state.devplan.plansFetched = false;
  },
  // Field-level merge (NOT a whole-object replace) so item/activePlan survive.
  successReducer(state, result) {
    state.devplan.plans = result.plans;
    state.devplan.plansFetched = true;
  },
  failReducer(state, error) {
    state.devplan.plansFetched = false;
    state.devplan.error = String(error);
  },
});

// GET /api/devplan/plans/<code>/me — the whole tree + this user's progression, for
// ONE tree. `:planCode` is filled from the payload by ApiCaller.
export const fetchDevPlan = appManager.createApi<
  { planCode: string },
  DevPlanState,
  AppState
>('FETCH_DEV_PLAN', {
  method: 'GET',
  path: '/devplan/plans/:planCode/me',
  successReducer(state, result) {
    state.devplan.item = result;
    state.devplan.itemPlanCode = result.plan?.code;
    state.devplan.fetched = true;
    state.devplan.error = undefined;
  },
  failReducer(state, error) {
    state.devplan.fetched = false;
    state.devplan.error = String(error);
  },
});

// POST /api/devplan/plans/<code>/attempt — ingest one scorecard envelope (from the
// iframe bridge or the dev force-pass); the backend appends the attempt for that
// tree and returns the fresh /me, which we store so the tree re-renders.
export const postDevAttempt = appManager.createApi<
  VegaAttemptPayload & { planCode: string },
  DevPlanState,
  AppState
>('POST_DEV_ATTEMPT', {
  method: 'POST',
  path: '/devplan/plans/:planCode/attempt',
  successReducer(state, result) {
    state.devplan.item = result;
    state.devplan.itemPlanCode = result.plan?.code;
    state.devplan.fetched = true;
    state.devplan.error = undefined;
  },
  // MIN-12: a failed attempt (e.g. a 404 on a just-hidden tree) isn't silent.
  failReducer(state, error) {
    state.devplan.error = String(error);
  },
});

// POST /api/devplan/plans/<code>/nodes/<internalCode>/launch — the solo room:
// start (or re-enter) this trainee's own session for the node's NEXT rank. The
// explicit scenario picks override the default. Recommendation picks also carry
// their approved recommendation item ID. The view consumes the returned session intent.
export const launchNodeSession = appManager.createApi<
  {
    planCode: string;
    internalCode: string;
    blueprint_slug?: string;
    recommendation_item_id?: number;
  },
  {
    session_id?: number;
    resumed?: boolean;
    code?: string;
    coins?: number;
    /** Absent while the frontend is paired with an older backend. */
    selection?: DevPlanLaunchSelection;
  },
  AppState
>('LAUNCH_DEVPLAN_NODE_SESSION', {
  method: 'POST',
  path: '/devplan/plans/:planCode/nodes/:internalCode/launch',
  startReducer(state) {
    state.devplan.error = undefined;
    state.devplan.launch = undefined;
  },
  successReducer(state, result) {
    // The Development Plan's abortable launch hook follows preparation; a
    // pending response must never become a /session/undefined navigation.
    if (result.code === 'assessment_preparing') return;
    if (!Number.isInteger(result.session_id) || result.session_id <= 0) {
      state.devplan.error =
        'The session start could not be confirmed. Refresh the plan and try again.';
      return;
    }
    state.devplan.launch = {
      sessionId: result.session_id,
      resumed: Boolean(result.resumed),
      selection: result.selection,
    };
    state.devplan.error = undefined;
  },
  failReducer(state, error) {
    state.devplan.error =
      typeof error === 'string'
        ? error
        : String(
            (error as { detail?: unknown })?.detail ||
              'Unable to start this scenario. Refresh the plan and try again.',
          );
  },
});

// Clear the navigation intent once consumed (or abandoned).
export const clearNodeLaunch = appManager.createLocalEvent(
  'CLEAR_DEVPLAN_NODE_LAUNCH',
  state => {
    state.devplan.launch = undefined;
  },
);

// POST /api/devplan/plans/<code>/nodes/<internalCode>/purchase — buy the node
// with coins (the coin economy's one gate). The response IS the fresh /me
// payload (plus a `purchase` receipt), so storing it re-renders the tree,
// wallet and ownership in one round trip.
export const purchaseNode = appManager.createApi<
  { planCode: string; internalCode: string },
  DevPlanState & { purchase?: { node: string; bought: boolean; cost: number } },
  AppState
>('PURCHASE_DEVPLAN_NODE', {
  method: 'POST',
  path: '/devplan/plans/:planCode/nodes/:internalCode/purchase',
  successReducer(state, result) {
    state.devplan.item = result;
    state.devplan.itemPlanCode = result.plan?.code;
    state.devplan.fetched = true;
    state.devplan.error = undefined;
  },
  failReducer(state, error) {
    state.devplan.error = String(error);
  },
});
